import { useEffect, useRef } from 'react';
import { trpc } from '../lib/trpc';

interface DrillDownUser {
  id: string;
  name: string | null;
  platform: string;
  platformUserId: string;
  subscriptionStatus: string | null;
  churnRisk: string | null;
  engagementScore: number | null;
  createdAt: string | null;
  updatedAt: string | null;
}

interface UserDrillDownModalProps {
  isOpen: boolean;
  onClose: () => void;
  date: string;
  type: 'new' | 'churned';
  botId?: string;
}

/**
 * Modal component for displaying drill-down user data from the User Growth chart.
 *
 * Shows a list of new or churned users for a specific date when a chart
 * bar or data point is clicked.
 */
export function UserDrillDownModal({
  isOpen,
  onClose,
  date,
  type,
  botId,
}: UserDrillDownModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);

  // Fetch users for the selected date
  const { data, isLoading, error } = trpc.dashboard.usersByDate.useQuery(
    { date, type, botId },
    { enabled: isOpen }
  );

  // Handle escape key to close modal
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Handle click outside modal to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      const timeoutId = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 0);
      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen, onClose]);

  // Prevent body scrolling when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getSubscriptionBadgeStyle = (status: string | null) => {
    const baseStyle = {
      padding: '4px 8px',
      borderRadius: '6px',
      fontSize: '11px',
      fontWeight: 600,
      textTransform: 'uppercase' as const,
    };

    switch (status) {
      case 'ACTIVE':
        return { ...baseStyle, backgroundColor: 'rgba(90, 138, 94, 0.12)', color: '#5A8A5E' };
      case 'TRIAL':
        return { ...baseStyle, backgroundColor: 'rgba(90, 106, 138, 0.12)', color: '#5A6A8A' };
      case 'CANCELLED':
        return { ...baseStyle, backgroundColor: 'rgba(139, 90, 80, 0.12)', color: '#8B5A50' };
      case 'EXPIRED':
        return { ...baseStyle, backgroundColor: 'rgba(155, 123, 60, 0.12)', color: '#9B7B3C' };
      default:
        return { ...baseStyle, backgroundColor: 'rgba(93, 86, 78, 0.08)', color: '#5C564E' };
    }
  };

  const getChurnRiskBadgeStyle = (risk: string | null) => {
    const baseStyle = {
      padding: '4px 8px',
      borderRadius: '6px',
      fontSize: '11px',
      fontWeight: 600,
    };

    switch (risk) {
      case 'HIGH':
        return { ...baseStyle, backgroundColor: 'rgba(139, 90, 80, 0.12)', color: '#8B5A50' };
      case 'MEDIUM':
        return { ...baseStyle, backgroundColor: 'rgba(155, 123, 60, 0.12)', color: '#9B7B3C' };
      case 'LOW':
        return { ...baseStyle, backgroundColor: 'rgba(90, 138, 94, 0.12)', color: '#5A8A5E' };
      default:
        return { ...baseStyle, backgroundColor: 'rgba(93, 86, 78, 0.08)', color: '#5C564E' };
    }
  };

  const getPlatformIcon = (platform: string) => {
    switch (platform.toLowerCase()) {
      case 'whatsapp':
        return '💬';
      case 'telegram':
        return '✈️';
      case 'web':
        return '🌐';
      default:
        return '📱';
    }
  };

  return (
    <div style={styles.overlay}>
      <div ref={modalRef} style={styles.modal}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.headerLeft}>
            <span style={styles.headerIcon}>
              {type === 'new' ? '📈' : '📉'}
            </span>
            <div>
              <h2 style={styles.title}>
                {type === 'new' ? 'New Users' : 'Churned Users'}
              </h2>
              <p style={styles.subtitle}>{formatDate(date)}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            style={styles.closeButton}
            aria-label="Close modal"
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div style={styles.content}>
          {isLoading && (
            <div style={styles.loadingContainer}>
              <span style={styles.spinner} />
              <p style={styles.loadingText}>Loading users...</p>
            </div>
          )}

          {error && (
            <div style={styles.errorMessage}>
              Failed to load users. Please try again.
            </div>
          )}

          {data && data.users.length === 0 && (
            <div style={styles.emptyState}>
              <span style={styles.emptyIcon}>
                {type === 'new' ? '👤' : '👋'}
              </span>
              <p style={styles.emptyText}>
                No {type === 'new' ? 'new' : 'churned'} users on this day
              </p>
            </div>
          )}

          {data && data.users.length > 0 && (
            <>
              {/* Summary */}
              <div style={styles.summaryBox}>
                <div style={styles.summaryIcon}>
                  {type === 'new' ? '✨' : '📊'}
                </div>
                <div style={styles.summaryContent}>
                  <p style={styles.summaryTitle}>
                    {data.total} {type === 'new' ? 'new' : 'churned'} user{data.total !== 1 ? 's' : ''}
                  </p>
                  <p style={styles.summaryText}>
                    {type === 'new'
                      ? 'Users who signed up on this day'
                      : 'Users who cancelled or expired on this day'}
                  </p>
                </div>
              </div>

              {/* User List */}
              <div style={styles.userList}>
                {data.users.map((user: DrillDownUser) => (
                  <div key={user.id} style={styles.userCard}>
                    <div style={styles.userHeader}>
                      <div style={styles.userInfo}>
                        <span style={styles.platformIcon}>
                          {getPlatformIcon(user.platform)}
                        </span>
                        <div>
                          <p style={styles.userName}>
                            {user.name || 'Anonymous User'}
                          </p>
                          <p style={styles.userPlatformId}>
                            {user.platform}: {user.platformUserId}
                          </p>
                        </div>
                      </div>
                      <span style={getSubscriptionBadgeStyle(user.subscriptionStatus)}>
                        {user.subscriptionStatus || 'Unknown'}
                      </span>
                    </div>
                    <div style={styles.userMeta}>
                      <div style={styles.userMetaItem}>
                        <span style={styles.metaLabel}>Engagement:</span>
                        <span style={styles.metaValue}>{user.engagementScore ?? 0}%</span>
                      </div>
                      <div style={styles.userMetaItem}>
                        <span style={styles.metaLabel}>Churn Risk:</span>
                        <span style={getChurnRiskBadgeStyle(user.churnRisk)}>
                          {user.churnRisk || 'N/A'}
                        </span>
                      </div>
                      <div style={styles.userMetaItem}>
                        <span style={styles.metaLabel}>
                          {type === 'new' ? 'Joined:' : 'Updated:'}
                        </span>
                        <span style={styles.metaValue}>
                          {new Date(type === 'new' ? user.createdAt! : user.updatedAt!).toLocaleTimeString()}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div style={styles.footer}>
          <button
            type="button"
            onClick={onClose}
            style={styles.closeButtonFooter}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    width: '100%',
    maxWidth: '560px',
    maxHeight: '85vh',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottom: '1px solid rgba(93, 86, 78, 0.08)',
    flexShrink: 0,
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  headerIcon: {
    fontSize: '24px',
  },
  title: {
    fontSize: '18px',
    fontWeight: 600,
    color: '#3D3A36',
    margin: 0,
    fontFamily: '"Sentient", Georgia, serif',
  },
  subtitle: {
    fontSize: '13px',
    color: '#9B9285',
    margin: 0,
  },
  closeButton: {
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    border: 'none',
    background: 'rgba(93, 86, 78, 0.06)',
    cursor: 'pointer',
    color: '#706A62',
    fontSize: '20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background 0.15s ease',
  },
  content: {
    padding: '24px',
    overflowY: 'auto',
    flex: 1,
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '48px 24px',
    gap: '16px',
  },
  spinner: {
    width: '24px',
    height: '24px',
    border: '3px solid rgba(93, 86, 78, 0.15)',
    borderTopColor: '#3D3A36',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  loadingText: {
    fontSize: '14px',
    color: '#9B9285',
    margin: 0,
  },
  errorMessage: {
    padding: '12px 16px',
    backgroundColor: 'rgba(139, 90, 80, 0.1)',
    color: '#8B5A50',
    borderRadius: '10px',
    fontSize: '13px',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '48px 24px',
    gap: '12px',
  },
  emptyIcon: {
    fontSize: '36px',
    opacity: 0.5,
  },
  emptyText: {
    fontSize: '14px',
    color: '#9B9285',
    margin: 0,
  },
  summaryBox: {
    display: 'flex',
    gap: '12px',
    padding: '14px 16px',
    background: 'linear-gradient(135deg, rgba(90, 106, 138, 0.08) 0%, rgba(90, 138, 94, 0.08) 100%)',
    borderRadius: '10px',
    border: '1px solid rgba(90, 106, 138, 0.15)',
    marginBottom: '20px',
  },
  summaryIcon: {
    fontSize: '18px',
    flexShrink: 0,
  },
  summaryContent: {
    flex: 1,
  },
  summaryTitle: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#3D3A36',
    margin: '0 0 4px 0',
  },
  summaryText: {
    fontSize: '12px',
    color: '#706A62',
    lineHeight: '1.5',
    margin: 0,
  },
  userList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  userCard: {
    padding: '14px 16px',
    background: 'rgba(93, 86, 78, 0.03)',
    borderRadius: '10px',
    border: '1px solid rgba(93, 86, 78, 0.08)',
  },
  userHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '10px',
  },
  userInfo: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
  },
  platformIcon: {
    fontSize: '20px',
    marginTop: '2px',
  },
  userName: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#3D3A36',
    margin: 0,
  },
  userPlatformId: {
    fontSize: '12px',
    color: '#9B9285',
    margin: '2px 0 0 0',
  },
  userMeta: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '16px',
    paddingTop: '10px',
    borderTop: '1px solid rgba(93, 86, 78, 0.06)',
  },
  userMetaItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  metaLabel: {
    fontSize: '11px',
    color: '#9B9285',
  },
  metaValue: {
    fontSize: '12px',
    color: '#5C564E',
    fontWeight: 500,
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    padding: '16px 24px',
    borderTop: '1px solid rgba(93, 86, 78, 0.08)',
    flexShrink: 0,
  },
  closeButtonFooter: {
    padding: '12px 24px',
    backgroundColor: '#3D3A36',
    color: '#FAF9F7',
    border: 'none',
    borderRadius: '10px',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'all 0.15s ease',
  },
};

export default UserDrillDownModal;
